import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchThread, sendChatMessage, type ChatMessage, type SsePayload, type ThreadResponse } from '../services/chatApi';

interface ChatPanelProps {
  open: boolean;
  onClose: () => void;
}

// In-flight turn state, separate from persisted messages.
interface PendingTurn {
  userText: string;
  toolCalls: Array<{
    tool_use_id: string;
    tool_name: string;
    input: any;
    ok?: boolean;
    error?: string | null;
    result_preview?: string;
    truncated_at?: number | null;
  }>;
  finalText: string | null;
  cost_usd: number | null;
  error: string | null;
  finishReason: string | null;
}

export function ChatPanel({ open, onClose }: ChatPanelProps) {
  const [thread, setThread] = useState<ThreadResponse | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<PendingTurn | null>(null);
  const [overrideHardCap, setOverrideHardCap] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Load thread + history on open.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await fetchThread();
        setThread(r);
      } catch (e: any) {
        setThread({
          thread_id: '', messages: [],
          cost: { month_to_date_usd: 0, soft_threshold_usd: 0, hard_threshold_usd: 0, soft_breached: false, hard_breached: false },
        });
      }
    })();
  }, [open]);

  // Auto-scroll to bottom whenever messages or pending state changes.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread?.messages.length, pending?.toolCalls.length, pending?.finalText]);

  const estTokens = useMemo(() => Math.ceil(draft.length / 4), [draft]);

  async function send() {
    if (!draft.trim() || busy) return;
    if (thread?.cost.hard_breached && !overrideHardCap) return;

    const userText = draft.trim();
    setDraft('');
    setBusy(true);
    setPending({ userText, toolCalls: [], finalText: null, cost_usd: null, error: null, finishReason: null });

    // Optimistically append the user message to the visible history
    // so the operator sees it immediately. Server persists it on entry.
    setThread(t => t ? ({
      ...t,
      messages: [...t.messages, {
        id: 'pending-user-' + Date.now(), role: 'user',
        content: [{ type: 'text', text: userText }],
        model: null, input_tokens: null, output_tokens: null,
        cache_read_tokens: null, cache_write_tokens: null, cost_usd: null,
        tool_calls: [], finish_reason: null, created_at: new Date().toISOString(),
      }],
    }) : t);

    try {
      await sendChatMessage(userText, handleSseEvent, { overrideHardCap });
    } catch (e: any) {
      setPending(p => p ? { ...p, error: e?.message ?? String(e) } : p);
    }
    // Reload the thread so we get the canonical persisted assistant message,
    // then clear the pending turn.
    try {
      const r = await fetchThread();
      setThread(r);
    } catch {}
    setPending(null);
    setBusy(false);
    setOverrideHardCap(false);
  }

  function handleSseEvent(e: SsePayload) {
    setPending(prev => {
      if (!prev) return prev;
      if (e.event === 'tool_start') {
        return { ...prev, toolCalls: [...prev.toolCalls, { tool_use_id: e.data.tool_use_id, tool_name: e.data.tool_name, input: e.data.input }] };
      }
      if (e.event === 'tool_end') {
        return { ...prev, toolCalls: prev.toolCalls.map(t =>
          t.tool_use_id === e.data.tool_use_id
            ? { ...t, ok: e.data.ok, error: e.data.error, result_preview: e.data.result_preview, truncated_at: e.data.truncated_at }
            : t
        )};
      }
      if (e.event === 'final_text') {
        return { ...prev, finalText: e.data.text };
      }
      if (e.event === 'done') {
        return { ...prev, cost_usd: e.data.cost_usd, finishReason: e.data.finish_reason };
      }
      if (e.event === 'error') {
        return { ...prev, error: e.data.message };
      }
      return prev;
    });
  }

  if (!open) return null;
  const cost = thread?.cost;

  return (
    <div className="fixed top-0 right-0 bottom-0 z-40 w-[480px] max-w-[92vw] bg-bg-card border-l border-border flex flex-col shadow-2xl">
      {/* Header */}
      <div className="px-5 py-3 border-b border-border flex items-center justify-between shrink-0">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-text-faint">Mineral Intelligence</div>
          <div className="font-display text-[15px] text-text">Chat <span className="text-text-dim text-[12px]">· Opus 4.7</span></div>
        </div>
        <div className="flex items-center gap-3">
          {cost && (
            <span title={`Month-to-date chat spend. Soft warn at $${cost.soft_threshold_usd}, hard cap at $${cost.hard_threshold_usd}.`}
              className={
                'text-[11px] font-mono px-2 py-0.5 rounded border ' +
                (cost.hard_breached ? 'border-red/50 text-red bg-red/10' :
                 cost.soft_breached ? 'border-accent/40 text-accent bg-accent-soft' :
                 'border-border text-text-faint')
              }>
              ${cost.month_to_date_usd.toFixed(2)} mtd
            </span>
          )}
          <button onClick={onClose} className="text-text-dim hover:text-text text-2xl leading-none">×</button>
        </div>
      </div>

      {/* Soft / hard cost banners */}
      {cost?.hard_breached && (
        <div className="px-4 py-2 bg-red/10 border-b border-red/30 text-[12px] text-red">
          Monthly chat budget exceeded ${cost.hard_threshold_usd}. Sends are blocked until you override.
          <label className="block mt-1 text-[11px] font-mono">
            <input type="checkbox" checked={overrideHardCap} onChange={e => setOverrideHardCap(e.target.checked)} className="mr-1.5" />
            Override for this turn
          </label>
        </div>
      )}
      {cost && cost.soft_breached && !cost.hard_breached && (
        <div className="px-4 py-2 bg-accent-soft border-b border-accent/30 text-[11px] text-accent-bright">
          Approaching ${cost.hard_threshold_usd} monthly cap (currently ${cost.month_to_date_usd.toFixed(2)})
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {thread?.messages.length === 0 && !pending && (
          <div className="text-[12px] text-text-faint font-mono text-center py-8 leading-relaxed">
            <div className="text-text-dim mb-2">Ask anything about the corpus.</div>
            <div>Try:</div>
            <div className="mt-1 text-text-ghost">"Who has surname Lueking?"</div>
            <div className="text-text-ghost">"Lease velocity in T4N R62W last quarter"</div>
            <div className="text-text-ghost">"Who's buying mineral deeds in DJ Basin?"</div>
          </div>
        )}

        {thread?.messages.map(m => (
          <PersistedMessage key={m.id} m={m} />
        ))}

        {pending && (
          <PendingTurnView pending={pending} />
        )}
      </div>

      {/* Input bar */}
      <div className="border-t border-border px-3 py-3 shrink-0">
        <div className="relative">
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
            }}
            placeholder="Ask about parties, AOIs, pool orders, leases, mineral deeds…"
            rows={3}
            disabled={busy}
            className="w-full bg-bg-input border border-border rounded px-3 py-2 text-[13px] text-text outline-none focus:border-accent resize-none disabled:opacity-50"
          />
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-[10px] text-text-faint font-mono">
            {busy ? 'thinking…' : `~est ${estTokens.toLocaleString()} tokens · ⌘+Enter to send`}
          </span>
          <button onClick={send}
            disabled={busy || !draft.trim() || (cost?.hard_breached && !overrideHardCap)}
            className="px-4 py-1.5 text-[12px] bg-accent text-bg font-medium rounded hover:bg-accent-bright disabled:opacity-50">
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Rendering a persisted message from history ────────────────────
function PersistedMessage({ m }: { m: ChatMessage }) {
  if (m.role === 'user') {
    const text = (m.content ?? []).filter((b: any) => b.type === 'text').map((b: any) => b.text).join('');
    return (
      <div className="bg-bg-input/40 border border-border rounded px-3 py-2 text-[13px] text-text">{text}</div>
    );
  }
  if (m.role === 'assistant') {
    const text = (m.content ?? []).filter((b: any) => b.type === 'text').map((b: any) => b.text).join('');
    const tools = m.tool_calls ?? [];
    return (
      <div>
        {tools.length > 0 && (
          <div className="mb-1 space-y-0.5">
            {tools.map((t: any, i: number) => (
              <div key={i} className="text-[10px] font-mono text-text-faint">
                {t.ok === false
                  ? <span className="text-red">✗ {t.tool_name}: {t.error}</span>
                  : <span>· {t.tool_name}{t.rows != null ? ` (${t.rows} rows${t.truncated_at ? `, truncated at ${t.truncated_at}` : ''})` : ''}</span>}
              </div>
            ))}
          </div>
        )}
        <div className="bg-bg-card-soft border border-border rounded px-3 py-2 text-[13px] text-text">
          <MarkdownBody text={text} />
        </div>
        {(m.input_tokens || m.cost_usd) && (
          <div className="mt-1 text-[10px] font-mono text-text-faint">
            {m.input_tokens ?? '?'}→{m.output_tokens ?? '?'} tokens
            {m.cache_read_tokens ? ` · ${m.cache_read_tokens} cached` : ''}
            {m.cost_usd ? ` · $${parseFloat(m.cost_usd).toFixed(4)}` : ''}
          </div>
        )}
      </div>
    );
  }
  return null;
}

// ── Rendering an in-flight turn ───────────────────────────────────
function PendingTurnView({ pending }: { pending: PendingTurn }) {
  return (
    <div>
      {pending.toolCalls.length > 0 && (
        <div className="mb-1 space-y-0.5">
          {pending.toolCalls.map((t, i) => (
            <div key={i} className="text-[10px] font-mono text-text-faint">
              {t.ok === undefined
                ? <span className="text-accent">… running {t.tool_name}</span>
                : t.ok === false
                  ? <span className="text-red">✗ {t.tool_name}: {t.error}</span>
                  : <span>· {t.tool_name} {t.result_preview ? `(${t.result_preview})` : ''}</span>}
            </div>
          ))}
        </div>
      )}
      {pending.finalText ? (
        <div className="bg-bg-card-soft border border-border rounded px-3 py-2 text-[13px] text-text">
          <MarkdownBody text={pending.finalText} />
        </div>
      ) : !pending.error ? (
        <div className="bg-bg-card-soft border border-border rounded px-3 py-2 text-[12px] text-text-faint italic font-mono">
          thinking…
        </div>
      ) : null}
      {pending.error && (
        <div className="bg-red/10 border border-red/30 rounded px-3 py-2 text-[12px] text-red">
          {pending.error}
        </div>
      )}
      {pending.finishReason === 'max_tool_calls' && (
        <div className="mt-1 text-[10px] text-amber font-mono">⚠ Hit max tool calls — answer may be incomplete; consider narrowing the question.</div>
      )}
      {pending.cost_usd != null && (
        <div className="mt-1 text-[10px] font-mono text-text-faint">turn cost ${pending.cost_usd.toFixed(4)}</div>
      )}
    </div>
  );
}

// ── Minimal Markdown renderer (links + bold + italic + bullets) ───
// Avoids pulling a full markdown library for v0.1. Anthropic-style
// responses with Markdown render cleanly through a small AST-free
// transformer. Handles: **bold**, *italic*, [text](url), - bullets.
function MarkdownBody({ text }: { text: string }) {
  const lines = text.split('\n');
  const blocks: React.ReactNode[] = [];
  let listBuffer: string[] = [];
  const flushList = () => {
    if (listBuffer.length === 0) return;
    blocks.push(
      <ul key={`ul-${blocks.length}`} className="list-disc pl-5 my-1.5 space-y-0.5">
        {listBuffer.map((l, i) => <li key={i}><InlineMarkdown text={l} /></li>)}
      </ul>
    );
    listBuffer = [];
  };
  for (const raw of lines) {
    const line = raw.replace(/^\s+$/, '');
    if (line.startsWith('- ')) {
      listBuffer.push(line.slice(2));
    } else {
      flushList();
      if (line === '') blocks.push(<div key={`sp-${blocks.length}`} className="h-1" />);
      else blocks.push(<p key={`p-${blocks.length}`} className="my-1"><InlineMarkdown text={line} /></p>);
    }
  }
  flushList();
  return <>{blocks}</>;
}

function InlineMarkdown({ text }: { text: string }) {
  // Order: links first (to consume their brackets), then bold, then italic.
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/;
  while (remaining.length > 0) {
    const m = linkRe.exec(remaining);
    if (!m) { parts.push(renderEmphasis(remaining, key++)); break; }
    if (m.index > 0) parts.push(renderEmphasis(remaining.slice(0, m.index), key++));
    const href = m[2];
    if (href.startsWith('/')) {
      parts.push(<Link key={key++} to={href} className="text-accent hover:text-accent-bright underline">{m[1]}</Link>);
    } else {
      parts.push(<a key={key++} href={href} target="_blank" rel="noreferrer" className="text-accent hover:text-accent-bright underline">{m[1]}</a>);
    }
    remaining = remaining.slice(m.index + m[0].length);
  }
  return <>{parts}</>;
}

function renderEmphasis(s: string, key: number): React.ReactNode {
  // **bold** and *italic* — single pass.
  const parts: React.ReactNode[] = [];
  let buf = s;
  let i = 0;
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*)/;
  while (buf.length > 0) {
    const m = re.exec(buf);
    if (!m) { parts.push(<span key={`t${key}-${i++}`}>{buf}</span>); break; }
    if (m.index > 0) parts.push(<span key={`t${key}-${i++}`}>{buf.slice(0, m.index)}</span>);
    if (m[2] != null) parts.push(<strong key={`b${key}-${i++}`}>{m[2]}</strong>);
    else if (m[3] != null) parts.push(<em key={`i${key}-${i++}`}>{m[3]}</em>);
    buf = buf.slice(m.index + m[0].length);
  }
  return <>{parts}</>;
}
